import { saveAs } from 'file-saver';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { getPdfMake } from './PDFMakeFactory';
import type { UserRegistrationInfo } from '@/services/security/models/registration';
import type { AxiosHeaders, AxiosResponse } from 'axios';
import { ContentDispositionFilename } from '@/constants/contentDispositionFileName';
import type { ExportResponse } from './report/models';

export function getFileName(headers: AxiosHeaders): string {
  const contentDisposition = headers.get('content-disposition');

  return (contentDisposition + '')?.match(ContentDispositionFilename)?.[1] || '';
}

export function generateDownloadResponse<T>(
  data: T,
  response: AxiosResponse<T>,
  formatJson = false,
  type?: string
): ExportResponse {
  const headers = response.headers as AxiosHeaders;
  const fileName = getFileName(headers);
  const contentType = headers['content-type'];
  const formattedData = formatJson ? JSON.stringify(data, null, 2) : data;
  const blob = new Blob([formattedData as BlobPart], type ? { type } : undefined);
  return { blob, fileName, contentType };
}

export function downloadFile(data: Blob, fileName: string): void {
  saveAs(data, fileName);
}

export async function exportRegistration(
  registration: UserRegistrationInfo,
  headerImage: HTMLImageElement,
  t: (key: string) => string
) {
  const PdfMake = await getPdfMake();

  const header = [];
  if (!browserIE11()) {
    header.push({
      image: getBase64Image(headerImage),
      width: 40,
      height: 65,
      alignment: 'left'
    });
  }
  header.push({
    text: '\n\n' + 'Република Србија' + '\n' + 'МИНИСТАРСТВО ФИНАНСИЈА' + '\n' + 'Управа за трезор',
    fontSize: 10
  });
  header.push({
    text: '\n\n' + 'Образац ' + t('serviceShortName') + '-1' + '\n' + t('serviceLongName'),
    fontSize: 10,
    alignment: 'right'
  });

  const docDefinition = {
    content: [
      {
        columns: header
      },
      {
        text: '\n\n' + t('userRegistration.registrationFormTitle') + '\n\n\n',
        style: 'header'
      },
      { text: t('register.legalEntityInfo') + '\n\n', style: 'subheader' },
      { text: t('register.budgetIdNumber') + ' : ', fontSize: 12 },
      { text: registration.budgetIdNumber + '\n\n', style: 'value' },
      // { text: t('register.budgetIdNumberFootnote') + '\n\n', style: 'small' },
      { text: t('register.companyName') + ' : ', fontSize: 12 },
      { text: registration.name + '\n\n', style: 'value' },
      {
        columns: [
          [
            { text: t('register.companyNumber') + ' : ', fontSize: 12 },
            { text: registration.companyNumber + '\n\n', style: 'value' }
          ],
          [
            { text: t('register.taxIdNumber') + ' : ', fontSize: 12 },
            { text: registration.taxIdNumber + '\n\n', style: 'value' }
          ]
        ]
      },
      { text: t('register.address') + ' : ', fontSize: 12 },
      { text: registration.address + '\n\n', style: 'value' },
      {
        columns: [
          [
            { text: t('register.email') + ' : ', fontSize: 12 },
            { text: registration.email + '\n\n', style: 'value' }
          ],
          [
            { text: t('general.phone') + ' : ', fontSize: 12 },
            { text: registration.phoneNumber + '\n\n', style: 'value' }
          ]
        ]
      },

      { text: t('register.adminInfo') + '\n\n', style: 'subheader' },
      { text: t('register.name') + ' : ', fontSize: 12 },
      { text: registration.adminName + '\n\n', style: 'value' },
      { text: t('register.identityNumber') + ' : ', fontSize: 12 },
      { text: registration.adminIdentityNumber + '\n\n', style: 'value' },
      {
        columns: [
          [
            { text: t('register.email') + ' : ', fontSize: 12 },
            { text: registration.adminEmail + '\n\n', style: 'value' }
          ],
          [
            { text: t('general.phone') + ' : ', fontSize: 12 },
            { text: registration.adminPhoneNumber + '\n\n\n', style: 'value' }
          ]
        ]
      },
      { text: t('register.treasuryBranchInfo') + '\n\n', style: 'subheader' },
      { text: t('register.treasuryBranch') + ' : ', fontSize: 12 },
      { text: registration.treasuryBranch + '\n', style: 'value' },
      {
        columns: [
          {
            text: '_____________________________',
            fontSize: 12,
            alignment: 'right',
            margin: [0, 35, 15, 5]
          }
        ]
      },
      {
        columns: [
          {
            text: 'М.П. Потпис одговорног лица' + '\n\n\n',
            fontSize: 11,
            alignment: 'right',
            margin: [0, 0, 15, 0]
          }
        ]
      }
    ],
    styles: {
      header: {
        fontSize: 16,
        bold: true,
        alignment: 'center'
      },
      value: {
        fontSize: 12,
        bold: true
      },
      subheader: {
        fontSize: 14,
        bold: true
      },
      quote: {
        italics: true
      },
      small: {
        fontSize: 9
      }
    }
  };
  PdfMake.createPdf(docDefinition as TDocumentDefinitions).download('nalog.pdf');
}

export async function exportAdminForm(
  registration: UserRegistrationInfo,
  headerImage: HTMLImageElement,
  t: (key: string) => string
) {
  const PdfMake = await getPdfMake();

  const header = [];
  if (!browserIE11()) {
    header.push({
      image: getBase64Image(headerImage),
      width: 40,
      height: 65,
      alignment: 'left'
    });
  }
  header.push({
    text: '\n\n' + 'Република Србија' + '\n' + 'МИНИСТАРСТВО ФИНАНСИЈА' + '\n' + 'Управа за трезор',
    fontSize: 10
  });
  header.push({
    text: '\n\n' + 'Образац' + t('serviceShortName') + '-2' + '\n' + t('serviceLongName'),
    fontSize: 10,
    alignment: 'right'
  });

  const docDefinition = {
    content: [
      {
        columns: header
      },
      { text: '\n\n' + t('userRegistration.adminFormTitle') + '\n\n\n', style: 'header' },
      { text: t('register.legalEntityInfo') + '\n\n', style: 'subheader' },
      { text: t('register.budgetIdNumber') + ' : ', fontSize: 12 },
      { text: registration.budgetIdNumber + '\n\n', style: 'value' },
      { text: t('register.companyName') + ' : ', fontSize: 12 },
      { text: registration.name + '\n\n', style: 'value' },
      {
        columns: [
          [
            { text: t('register.companyNumber') + ' : ', fontSize: 12 },
            { text: registration.companyNumber + '\n\n', style: 'value' }
          ],
          [
            { text: t('register.taxIdNumber') + ' : ', fontSize: 12 },
            { text: registration.taxIdNumber + '\n\n', style: 'value' }
          ]
        ]
      },
      { text: t('register.address') + ' : ', fontSize: 12 },
      { text: registration.address + '\n\n', style: 'value' },
      { text: t('register.adminInfo') + '\n\n', style: 'subheader' },
      { text: t('userAdministration.userLogin') + ' : ', fontSize: 12 },
      { text: registration.adminLogin + '\n\n', style: 'value' },
      { text: t('register.name') + ' : ', fontSize: 12 },
      { text: registration.adminName + '\n\n', style: 'value' },
      { text: t('register.email') + ' : ', fontSize: 12 },
      { text: registration.adminEmail + '\n\n', style: 'value' },
      {
        columns: [
          {
            text: '__________________________',
            fontSize: 12,
            alignment: 'left',
            margin: [15, 100, 0, 5]
          },
          {
            text: '_____________________________',
            fontSize: 12,
            alignment: 'right',
            margin: [0, 100, 15, 5]
          }
        ]
      },
      {
        columns: [
          {
            text: 'Предао у Управи за трезор',
            fontSize: 11,
            alignment: 'left',
            margin: [15, 0, 0, 0]
          },
          {
            text: 'Примио за правно лице/орган',
            fontSize: 11,
            alignment: 'right',
            margin: [0, 0, 15, 0]
          }
        ]
      }
    ],
    styles: {
      header: {
        fontSize: 16,
        bold: true,
        alignment: 'center'
      },
      value: {
        fontSize: 12,
        bold: true
      },
      subheader: {
        fontSize: 14,
        bold: true
      },
      quote: {
        italics: true
      },
      small: {
        fontSize: 8
      }
    }
  };
  PdfMake.createPdf(docDefinition as TDocumentDefinitions).download('nalog.pdf');
}

export function getBase64Image(img: HTMLImageElement): string {
  if (browserIE11()) {
    return '';
  }

  // Create an empty canvas element
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;

  // Copy the image contents to the canvas
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }
  ctx.drawImage(img, 0, 0);

  return canvas.toDataURL('image/png');
}

export function browserIE11() {
  return 'documentMode' in document && !!document.documentMode;
}

export function getExtensionFromFilename(filename: string): string {
  return filename.slice(filename.lastIndexOf('.')) || '';
}
